// White label (PHASE24): the look of the portal. Before sign-in it is the address's tenant (GET
// /api/public/branding: a tenant domain or <slug>.PLATFORM_DOMAIN, else the default tenant); after sign-in it
// is the signed-in tenant's (GET /api/auth/me). Applied to the page title, favicon, logo and primary colour.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { Headset } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { cn } from '@/lib/utils';
import { setLanguage } from '@/i18n';

export interface Branding {
  product_name: string; primary_color: string | null; logo_url: string | null;
  login_message: string | null; support_email: string | null;
  tenant?: { slug: string; name: string } | null;
  portal_default_language?: string;     // public branding only (PHASE26)
  app_icon_url?: string | null;          // public branding only: the installed app's icon (docs/PWA.md)
}

const DEFAULT: Branding = { product_name: 'Call Center', primary_color: null, logo_url: null, login_message: null, support_email: null };

// the signed-in tenant's branding, set from /auth/me (AdminLayout, AgentLayout)
export const useSessionBranding = create<{ branding: Branding | null; set: (b: Branding | null) => void }>((set) => ({
  branding: null, set: (branding) => set({ branding }),
}));

export function usePublicBranding() {
  return useQuery({ queryKey: ['public-branding'], queryFn: () => api<Branding>('/public/branding'), staleTime: 5 * 60_000 });
}

export function useBranding(): Branding {
  const signedIn = useAuth((s) => !!s.token);
  const session = useSessionBranding((s) => s.branding);
  const pub = usePublicBranding();
  return (signedIn && session) || pub.data || DEFAULT;
}

// black or white text on the colour
function foreground(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => { const x = c / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4 ? '#111111' : '#ffffff';
}

export function BrandingApplier() {
  const b = useBranding();
  const appIcon = usePublicBranding().data?.app_icon_url ?? null;
  React.useEffect(() => {
    document.title = b.product_name;
    const root = document.documentElement.style;
    if (b.primary_color) {
      root.setProperty('--primary', b.primary_color);
      root.setProperty('--primary-foreground', foreground(b.primary_color));
      root.setProperty('--ring', b.primary_color);
    } else {
      for (const k of ['--primary', '--primary-foreground', '--ring']) root.removeProperty(k);
    }
    const link = (rel: string) => {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); }
      return el;
    };
    const icon = link('icon');
    icon.removeAttribute('type'); icon.removeAttribute('sizes');
    icon.href = b.logo_url ?? '/icons/favicon-32.png';
    // installed app (docs/PWA.md): bar colour, and the home-screen icon on iPhone / iPad (the address's company)
    let theme = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!theme) { theme = document.createElement('meta'); theme.name = 'theme-color'; document.head.appendChild(theme); }
    theme.content = b.primary_color ?? '#171717';
    link('apple-touch-icon').href = appIcon ?? '/icons/apple-touch-icon.png';
  }, [b.product_name, b.primary_color, b.logo_url, appIcon]);
  return null;
}

// the logo, or the default headset icon
export function BrandLogo({ className }: { className?: string }) {
  const b = useBranding();
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [b.logo_url]);
  if (b.logo_url && !broken) return <img src={b.logo_url} alt="" className={cn('object-contain', className)} onError={() => setBroken(true)} data-testid="brand-logo" />;
  return <Headset className={className} />;
}

// the signed-in tenant's branding for layouts that do not read /auth/me themselves (the agent portal)
export function useMeBranding() {
  const uid = useAuth((s) => s.user?.id);
  const setB = useSessionBranding((s) => s.set);
  const setPermissions = useAuth((s) => s.setPermissions);
  const me = useQuery({ queryKey: ['me-branding', uid], enabled: !!uid, queryFn: () => api<{ branding?: Branding; language?: string; permissions?: string[] }>('/auth/me') });
  React.useEffect(() => {
    if (me.data?.branding) setB(me.data.branding);
    setLanguage(me.data?.language);
    // the agent's own rights (e.g. the CRM panel's actions); the admin layout loads them itself
    if (me.data?.permissions) setPermissions(me.data.permissions);
  }, [me.data, setB, setPermissions]);
}
